import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MovieIcon from '@mui/icons-material/Movie';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import React, { useState } from 'react';
import { TeamGroupTeamDto } from 'src/api/teamGroups';
import {
  CustomerCardDetails,
  CustomerCardPerson,
  buildCustomerCardPreview,
} from 'src/components/oth/CustomerCardDetails';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import styles from '../Information.module.css';
import {
  ContactListField,
  ContactListMember,
  FileDetails,
  InformationContentType,
  InformationSection,
  SectionValidationErrors,
} from '../types';
import { getFilePresentation } from '../utils/filePresentation';
import { hasDeclinedContactShare } from '../utils/contactListMembers';
import { EditableTeam } from '../utils/teamGroups';
import { RichTextEditor } from './RichTextEditor';
import { TeamGroupEditor } from './TeamGroupEditor';

type InformationEditorProps = {
  isLoading: boolean;
  success: string | null;
  savedFormExists: boolean;
  isSaving: boolean;
  canSave: boolean;
  isAnyUploading: boolean;
  sections: InformationSection[];
  uploadingBySection: Record<string, number>;
  sectionErrors: Record<string, SectionValidationErrors>;
  fileDetails: Record<string, FileDetails>;
  isFetchingFileDetails: boolean;
  contactListMembers: ContactListMember[];
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  onCancelEdit: () => void;
  onAddSection: () => void;
  onMoveSectionUp: (index: number) => void;
  onMoveSectionDown: (index: number) => void;
  onRemoveSection: (id: string) => void;
  onSectionTitleChange: (id: string, value: string) => void;
  onSectionContentChange: (id: string, value: string) => void;
  onSectionFilesChange: (id: string, fileList: FileList | null) => void;
  onRemoveFile: (sectionId: string, fileIndex: number) => void;
  onAddContent: (
    sectionId: string,
    contentType: InformationContentType
  ) => void;
  onRemoveContent: (
    sectionId: string,
    contentType: InformationContentType
  ) => void;
  onContactFieldsChange: (
    sectionId: string,
    fields: ContactListField[]
  ) => void;
  onExcludedContactsChange: (
    sectionId: string,
    excludedContactIds: string[]
  ) => void;
  openingFileId?: string | null;
  onOpenFile?: (fileId: string) => void;
  enableTeams?: boolean;
  teamGroupName?: string;
  onTeamGroupNameChange?: (name: string) => void;
  draftTeams?: EditableTeam[];
  previousTeams?: TeamGroupTeamDto[];
  onDraftTeamsChange?: (teams: EditableTeam[]) => void;
  teamContacts?: ContactListMember[];
  currentUserId?: string;
  circleId?: string;
  isLeader?: boolean;
};

const FileTypeIcon: React.FC<{ kind: 'pdf' | 'image' | 'video' | 'file' }> = ({
  kind,
}) => {
  if (kind === 'pdf') {
    return <PictureAsPdfIcon fontSize="small" />;
  }
  if (kind === 'image') {
    return <ImageIcon fontSize="small" />;
  }
  if (kind === 'video') {
    return <MovieIcon fontSize="small" />;
  }
  return <InsertDriveFileIcon fontSize="small" />;
};

const CONTACT_FIELD_OPTIONS: Array<{
  value: ContactListField;
  label: string;
}> = [
  { value: 'name', label: 'Name' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'company', label: 'Company' },
  { value: 'notes', label: 'Notes' },
];

const CONTACT_COLUMN_LABELS: Record<ContactListField, string> = {
  name: 'Navn',
  phone: 'Telefon',
  email: 'E-post',
  company: 'Firma',
  notes: 'Notater',
};

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

const participantLabel = (count: number) =>
  count === 1 ? '1 deltaker' : `${count} deltakere`;

const ContactListEditor: React.FC<{
  sectionId: string;
  members: ContactListMember[];
  contactFields: ContactListField[];
  excludedContactIds: string[];
  onExcludedContactsChange: (
    sectionId: string,
    excludedContactIds: string[]
  ) => void;
  onSelectMember?: (member: ContactListMember) => void;
}> = ({
  sectionId,
  members,
  contactFields,
  excludedContactIds,
  onExcludedContactsChange,
  onSelectMember,
}) => {
  const [showMore, setShowMore] = useState(false);
  const declinedCount = members.filter(hasDeclinedContactShare).length;
  const visibleCount = members.length - declinedCount;

  return (
    <div className={styles.contactListPreview}>
      {declinedCount > 0 && (
        <div className={styles.contactEditorBanner} role="status">
          <span className={styles.contactEditorLock} aria-hidden="true">
            <LockOutlinedIcon />
          </span>
          <div className={styles.contactEditorBannerText}>
            <strong>
              {participantLabel(visibleCount)} kan vises
              <span className={styles.contactEditorBannerDot}> • </span>
              <span className={styles.contactEditorOmitted}>
                {declinedCount} utelatt pga. manglende samtykke
              </span>
            </strong>
            <p>
              Deltakere uten samtykke vises ikke med kontaktinformasjon i
              deltakerappen.
              {showMore &&
                ' De kan ikke hukes av, og vises ikke med telefon eller e-post for andre deltakere.'}
            </p>
          </div>
          <button
            type="button"
            className={styles.consentLearnMore}
            onClick={() => setShowMore((open) => !open)}
            aria-expanded={showMore}
          >
            {showMore ? 'Les mindre' : 'Les mer'}
          </button>
        </div>
      )}
      {members.length === 0 ? (
        <p className={styles.contactListEmpty}>No circle members to display.</p>
      ) : (
        <>
          <div className={styles.contactListHeader}>
            <span className={styles.contactHeaderCheckbox} aria-hidden="true" />
            {contactFields.map((field) => (
              <span
                key={field}
                className={
                  field === 'name' ? styles.contactNameHeader : undefined
                }
              >
                {CONTACT_COLUMN_LABELS[field]}
              </span>
            ))}
          </div>
          {members.map((member) => {
            const declined = hasDeclinedContactShare(member);
            const isVisible =
              !declined && !excludedContactIds.includes(member.id);

            return (
              <div
                key={member.id}
                className={
                  declined
                    ? `${styles.contactListRow} ${styles.contactListRowDisabled}`
                    : onSelectMember
                      ? `${styles.contactListRow} ${styles.contactListRowClickable}`
                      : styles.contactListRow
                }
                onClick={
                  onSelectMember ? () => onSelectMember(member) : undefined
                }
                onKeyDown={
                  onSelectMember
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onSelectMember(member);
                        }
                      }
                    : undefined
                }
                role={onSelectMember ? 'button' : undefined}
                tabIndex={onSelectMember ? 0 : undefined}
              >
                <input
                  type="checkbox"
                  className={styles.contactVisibilityCheckbox}
                  checked={isVisible}
                  disabled={declined}
                  onChange={(event) => {
                    event.stopPropagation();
                    if (declined) {
                      return;
                    }
                    const nextExcludedContactIds = event.target.checked
                      ? excludedContactIds.filter((id) => id !== member.id)
                      : [...excludedContactIds, member.id];
                    onExcludedContactsChange(sectionId, nextExcludedContactIds);
                  }}
                  onClick={(event) => event.stopPropagation()}
                  aria-label={
                    declined
                      ? `${member.name} har ikke samtykket til deling`
                      : `Show ${member.name}`
                  }
                />
                {contactFields.includes('name') && (
                  <div className={styles.contactName}>
                    {member.avatarUrl ? (
                      <img
                        src={member.avatarUrl}
                        alt=""
                        className={styles.contactAvatar}
                      />
                    ) : (
                      <span className={styles.contactInitials}>
                        {getInitials(member.name)}
                      </span>
                    )}
                    <span className={styles.contactNameText}>
                      <span>{member.name}</span>
                      {declined && (
                        <span className={styles.contactNameHint}>
                          Har ikke samtykket til deling av kontaktinformasjon.
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {contactFields.includes('phone') && (
                  <span>{declined ? '—' : member.phone || '—'}</span>
                )}
                {contactFields.includes('email') && (
                  <span>{declined ? '—' : member.email || '—'}</span>
                )}
                {contactFields.includes('company') && (
                  <span>{member.company || '—'}</span>
                )}
                {contactFields.includes('notes') && (
                  <span>{declined ? '—' : member.notes || '—'}</span>
                )}
                {declined && (
                  <span className={styles.consentPill}>
                    <LockOutlinedIcon />
                    Ingen samtykke
                  </span>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export const InformationEditor: React.FC<InformationEditorProps> = ({
  isLoading,
  success,
  savedFormExists,
  isSaving,
  canSave,
  isAnyUploading,
  sections,
  uploadingBySection,
  sectionErrors,
  fileDetails,
  isFetchingFileDetails,
  contactListMembers,
  onSubmit,
  onCancelEdit,
  onAddSection,
  onMoveSectionUp,
  onMoveSectionDown,
  onRemoveSection,
  onSectionTitleChange,
  onSectionContentChange,
  onSectionFilesChange,
  onRemoveFile,
  onAddContent,
  onRemoveContent,
  onContactFieldsChange,
  onExcludedContactsChange,
  openingFileId,
  onOpenFile,
  enableTeams = false,
  teamGroupName = 'T-lag',
  onTeamGroupNameChange,
  draftTeams = [],
  previousTeams = [],
  onDraftTeamsChange,
  teamContacts,
  currentUserId,
  circleId,
  isLeader = false,
}) => {
  const [selectedCard, setSelectedCard] = useState<CustomerCardPerson | null>(
    null
  );

  const openCustomerCard = (member: ContactListMember, teamName?: string) => {
    setSelectedCard(
      buildCustomerCardPreview(member, contactListMembers, { teamName })
    );
  };

  return (
    <>
      <form onSubmit={onSubmit}>
        <div className={styles.header}>
          <div>
            {success && <div className={styles.successMessage}>{success}</div>}
          </div>

          <div className={styles.saveBar}>
            {savedFormExists && (
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={onCancelEdit}
                disabled={isSaving}
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={onAddSection}
            >
              + Add section
            </button>

            <button
              type="submit"
              className={styles.primaryButton}
              disabled={!canSave || isSaving || isAnyUploading}
            >
              {isSaving
                ? 'Saving...'
                : isAnyUploading
                  ? 'Uploading files...'
                  : 'Save form'}
            </button>
          </div>
        </div>

        {sections.length === 0 ? (
          <div className={styles.emptyState}>
            {isLoading ? (
              <div className={styles.previewLoading}>
                <MaterialCircularProgress height="auto" size={36} />
              </div>
            ) : (
              <p>
                No sections yet. Click <strong>&quot;Add section&quot;</strong>{' '}
                to start building your form.
              </p>
            )}
          </div>
        ) : (
          <div className={styles.sectionsList}>
            {sections.map((section, index) => {
              const isUploadingFiles =
                (uploadingBySection[section.id] ?? 0) > 0;
              const uploadSkeletonCount = uploadingBySection[section.id] ?? 0;
              const currentSectionErrors = sectionErrors[section.id];
              const titleError = currentSectionErrors?.title;
              const contentError = currentSectionErrors?.content;
              const hasErrors = !!titleError || !!contentError;
              const contentTypes = section.contentTypes ?? ['text', 'files'];
              const contactFields =
                section.contactFields ??
                CONTACT_FIELD_OPTIONS.map((o) => o.value);
              const excludedContactIds = section.excludedContactIds ?? [];
              const teamsUsedElsewhere = sections.some(
                (item) =>
                  item.id !== section.id && item.contentTypes.includes('teams')
              );

              return (
                <div className={styles.sectionCard} key={section.id}>
                  <div className={styles.sectionHeader}>
                    <div className={styles.sectionHeaderInfo}>
                      {hasErrors && (
                        <span
                          className={`${styles.sectionStatus} ${styles.sectionStatusError}`}
                        >
                          Needs attention
                        </span>
                      )}
                    </div>
                    <div className={styles.sectionHeaderActions}>
                      <button
                        type="button"
                        className={styles.iconChevronButton}
                        onClick={() => onMoveSectionUp(index)}
                        disabled={index === 0}
                        title="Move up"
                      >
                        <CaretUpOutlined />
                      </button>
                      <button
                        type="button"
                        className={styles.iconChevronButton}
                        onClick={() => onMoveSectionDown(index)}
                        disabled={index === sections.length - 1}
                        title="Move down"
                      >
                        <CaretDownOutlined />
                      </button>
                      <button
                        type="button"
                        className={styles.deleteButton}
                        onClick={() => onRemoveSection(section.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <div className={styles.sectionBody}>
                    <div className={styles.fieldGroup}>
                      <input
                        id={`section-title-${section.id}`}
                        className={`${styles.textInput} ${
                          titleError ? styles.inputError : ''
                        }`}
                        value={section.title}
                        onChange={(e) =>
                          onSectionTitleChange(section.id, e.target.value)
                        }
                        placeholder="Add your title & emojis here 🚀"
                        aria-invalid={!!titleError}
                        aria-describedby={
                          titleError
                            ? `section-title-error-${section.id}`
                            : undefined
                        }
                      />
                      {titleError && (
                        <p
                          id={`section-title-error-${section.id}`}
                          className={styles.fieldError}
                        >
                          {titleError}
                        </p>
                      )}
                    </div>

                    {contentTypes.includes('text') && (
                      <div className={styles.contentBlock}>
                        <div className={styles.contentBlockHeader}>
                          <span className={styles.contentBlockTitle}>
                            <TextSnippetOutlinedIcon fontSize="small" />
                            Text
                          </span>
                          <button
                            type="button"
                            className={styles.contentDeleteButton}
                            onClick={() => onRemoveContent(section.id, 'text')}
                            aria-label="Remove text"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </button>
                        </div>
                        <div
                          className={`${styles.richTextWrapper} ${
                            contentError ? styles.richTextWrapperError : ''
                          }`}
                        >
                          <RichTextEditor
                            value={section.content}
                            onChange={(html) =>
                              onSectionContentChange(section.id, html)
                            }
                            placeholder="Add new content here..."
                          />
                        </div>
                        {contentError && (
                          <p className={styles.fieldError}>{contentError}</p>
                        )}
                      </div>
                    )}

                    {contentTypes.includes('files') && (
                      <div className={styles.contentBlock}>
                        <div className={styles.contentBlockHeader}>
                          <span className={styles.contentBlockTitle}>
                            <AttachFileIcon fontSize="small" />
                            Files
                          </span>
                          <button
                            type="button"
                            className={styles.contentDeleteButton}
                            onClick={() => onRemoveContent(section.id, 'files')}
                            aria-label="Remove files"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </button>
                        </div>
                        <input
                          id={`files-${section.id}`}
                          type="file"
                          multiple
                          className={styles.fileInputHidden}
                          disabled={isUploadingFiles}
                          onChange={(e) => {
                            onSectionFilesChange(section.id, e.target.files);
                            e.currentTarget.value = '';
                          }}
                        />

                        <label
                          htmlFor={`files-${section.id}`}
                          className={`${styles.fileBrowseButton} ${
                            isUploadingFiles
                              ? styles.fileBrowseButtonUploading
                              : ''
                          }`}
                          aria-disabled={isUploadingFiles}
                        >
                          <span
                            className={
                              isUploadingFiles
                                ? styles.uploadingIcon
                                : undefined
                            }
                          >
                            <UploadFileIcon />
                          </span>
                          <span>
                            {isUploadingFiles
                              ? 'Uploading...'
                              : 'Drag and drop files here, or click to browse'}
                            <small className={styles.fileBrowseHint}>
                              PNG/JPG, MOV/MP4, PDF, Office files. Max 150 Mb.
                            </small>
                          </span>
                        </label>

                        {(section.files.length > 0 ||
                          uploadSkeletonCount > 0) && (
                          <ul className={styles.fileList}>
                            {Array.from({ length: uploadSkeletonCount }).map(
                              (_, skeletonIndex) => (
                                <li
                                  key={`${section.id}-upload-skeleton-${skeletonIndex}`}
                                  className={`${styles.fileListItem} ${styles.fileSkeletonItem}`}
                                  aria-hidden="true"
                                >
                                  <span className={styles.fileTypeIcon}>
                                    <span
                                      className={`${styles.skeletonBlock} ${styles.skeletonSquare}`}
                                    />
                                  </span>
                                  <div className={styles.fileMeta}>
                                    <span
                                      className={`${styles.skeletonBlock} ${styles.skeletonLine} ${styles.skeletonLinePrimary}`}
                                    />
                                    <span
                                      className={`${styles.skeletonBlock} ${styles.skeletonLine} ${styles.skeletonLineSecondary}`}
                                    />
                                  </div>
                                </li>
                              )
                            )}

                            {section.files.map((file, fileIndex) => {
                              const details = fileDetails[file.id];
                              const label =
                                file.name || details?.name || 'Attachment';
                              const contentType =
                                details?.contentType || file.contentType || '';
                              const { kind, fileTypeLabel } =
                                getFilePresentation(label, contentType);
                              const showFetchSkeleton =
                                isFetchingFileDetails && !details;
                              const isOpening = openingFileId === file.id;
                              const canOpen =
                                Boolean(onOpenFile) && !showFetchSkeleton;

                              return (
                                <li
                                  key={`${section.id}-file-${fileIndex}`}
                                  className={styles.fileListItem}
                                >
                                  <span
                                    className={styles.fileTypeIcon}
                                    aria-hidden="true"
                                  >
                                    {showFetchSkeleton ? (
                                      <span
                                        className={`${styles.skeletonBlock} ${styles.skeletonSquare}`}
                                      />
                                    ) : (
                                      <FileTypeIcon kind={kind} />
                                    )}
                                  </span>
                                  <div className={styles.fileMeta}>
                                    {showFetchSkeleton ? (
                                      <>
                                        <span
                                          className={`${styles.skeletonBlock} ${styles.skeletonLine} ${styles.skeletonLinePrimary}`}
                                        />
                                        <span
                                          className={`${styles.skeletonBlock} ${styles.skeletonLine} ${styles.skeletonLineSecondary}`}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        {canOpen ? (
                                          <button
                                            type="button"
                                            className={styles.fileName}
                                            onClick={() =>
                                              onOpenFile?.(file.id)
                                            }
                                            disabled={isOpening}
                                          >
                                            {label}
                                          </button>
                                        ) : (
                                          <span className={styles.fileName}>
                                            {label}
                                          </span>
                                        )}
                                        <span className={styles.fileTypeLabel}>
                                          {fileTypeLabel}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  {canOpen && (
                                    <button
                                      type="button"
                                      className={styles.downloadFileButton}
                                      onClick={() => onOpenFile?.(file.id)}
                                      disabled={isOpening}
                                      aria-label={`Open ${label}`}
                                    >
                                      <span>
                                        {isOpening ? 'Opening…' : 'Open'}
                                      </span>
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className={styles.removeFileButton}
                                    disabled={showFetchSkeleton}
                                    onClick={() =>
                                      onRemoveFile(section.id, fileIndex)
                                    }
                                  >
                                    Remove
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}

                    {contentTypes.includes('contactList') && (
                      <div className={styles.contentBlock}>
                        <div className={styles.contentBlockHeader}>
                          <div>
                            <span className={styles.contentBlockTitle}>
                              <GroupsIcon fontSize="small" />
                              Contact list
                            </span>
                            <p className={styles.contentBlockDescription}>
                              Automatically shows members of this circle. Choose
                              which fields to display.
                            </p>
                          </div>
                          <button
                            type="button"
                            className={styles.contentDeleteButton}
                            onClick={() =>
                              onRemoveContent(section.id, 'contactList')
                            }
                            aria-label="Remove contact list"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </button>
                        </div>
                        <div className={styles.contactFieldOptions}>
                          {CONTACT_FIELD_OPTIONS.map((option) => (
                            <label
                              key={option.value}
                              className={styles.contactFieldOption}
                            >
                              <input
                                type="checkbox"
                                checked={contactFields.includes(option.value)}
                                onChange={(event) => {
                                  const fields = event.target.checked
                                    ? [...contactFields, option.value]
                                    : contactFields.filter(
                                        (field) => field !== option.value
                                      );
                                  onContactFieldsChange(section.id, fields);
                                }}
                              />
                              {option.label}
                            </label>
                          ))}
                        </div>
                        <ContactListEditor
                          sectionId={section.id}
                          members={contactListMembers}
                          contactFields={contactFields}
                          excludedContactIds={excludedContactIds}
                          onExcludedContactsChange={onExcludedContactsChange}
                          onSelectMember={openCustomerCard}
                        />
                      </div>
                    )}

                    {enableTeams && contentTypes.includes('teams') && (
                      <div className={styles.contentBlock}>
                        <div className={styles.contentBlockHeader}>
                          <div>
                            <span className={styles.contentBlockTitle}>
                              <GroupsIcon fontSize="small" />
                              Team / Lag
                            </span>
                            <p className={styles.contentBlockDescription}>
                              Del inn deltakere i lag. Du kan gjøre dette
                              automatisk eller manuelt.
                            </p>
                          </div>
                          <button
                            type="button"
                            className={styles.contentDeleteButton}
                            onClick={() => onRemoveContent(section.id, 'teams')}
                            aria-label="Remove teams"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </button>
                        </div>
                        <TeamGroupEditor
                          groupName={teamGroupName}
                          onGroupNameChange={(name) =>
                            onTeamGroupNameChange?.(name)
                          }
                          contacts={teamContacts ?? contactListMembers}
                          teams={draftTeams}
                          previousTeams={previousTeams}
                          currentUserId={currentUserId}
                          onChange={(nextTeams) =>
                            onDraftTeamsChange?.(nextTeams)
                          }
                          onSelectMember={openCustomerCard}
                        />
                      </div>
                    )}
                  </div>

                  <div className={styles.addContentPanel}>
                    <div className={styles.addContentHeading}>
                      <AddCircleIcon />
                      <div>
                        <strong>Add content</strong>
                        <span>
                          Choose what you want to add to this section.
                        </span>
                      </div>
                    </div>
                    <div
                      className={
                        enableTeams
                          ? `${styles.addContentOptions} ${styles.addContentOptionsWithTeams}`
                          : styles.addContentOptions
                      }
                    >
                      <button
                        type="button"
                        className={styles.addContentButton}
                        disabled={contentTypes.includes('text')}
                        onClick={() => onAddContent(section.id, 'text')}
                      >
                        <TextSnippetOutlinedIcon fontSize="small" />
                        Text
                      </button>
                      <button
                        type="button"
                        className={styles.addContentButton}
                        disabled={contentTypes.includes('files')}
                        onClick={() => onAddContent(section.id, 'files')}
                      >
                        <AttachFileIcon fontSize="small" />
                        Files
                      </button>
                      <button
                        type="button"
                        className={styles.addContentButton}
                        disabled={contentTypes.includes('contactList')}
                        onClick={() => onAddContent(section.id, 'contactList')}
                      >
                        <GroupsIcon fontSize="small" />
                        Contact list
                      </button>
                      {enableTeams && (
                        <button
                          type="button"
                          className={styles.addContentButton}
                          disabled={
                            contentTypes.includes('teams') || teamsUsedElsewhere
                          }
                          onClick={() => onAddContent(section.id, 'teams')}
                        >
                          <GroupsIcon fontSize="small" />
                          Teams
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </form>
      <CustomerCardDetails
        open={Boolean(selectedCard)}
        tribeId={circleId}
        preview={selectedCard}
        viewerRole={isLeader ? 'leader' : 'member'}
        currentUserId={currentUserId}
        onClose={() => setSelectedCard(null)}
      />
    </>
  );
};
